/**
 * Recursively strip `undefined` values from an object before writing
 * to Firestore. Firestore rejects documents that contain `undefined`
 * anywhere with: "Function updateDoc() called with invalid data.
 * Unsupported field value: undefined".
 *
 * Use this in any direct `updateDoc` / `setDoc` call site that writes
 * caller-supplied data — especially places that merge two records and
 * may inherit undefined fields from either source.
 *
 * Notes:
 * - Returns a new object/array; does not mutate the input.
 * - `null` is preserved (Firestore accepts null).
 * - Plain objects only — Maps/Sets/Date/etc. are returned as-is, since
 *   Firestore handles those (or rejects them) on its own.
 * - Emits a single `console.warn` per top-level call listing every
 *   stripped path. Useful for hunting down upstream producers that
 *   explicitly write `{ key: undefined }` — those are usually bugs the
 *   strip is silently papering over.
 */
const stripUndefinedInner = (value, path, stripped) => {
  if (value === null || value === undefined) return value;
  if (Array.isArray(value)) {
    return value.map((v, i) =>
      stripUndefinedInner(v, `${path}[${i}]`, stripped)
    );
  }
  if (typeof value === 'object' && value.constructor === Object) {
    const cleaned = {};
    for (const [key, val] of Object.entries(value)) {
      const childPath = path ? `${path}.${key}` : key;
      if (val === undefined) {
        stripped.push(childPath);
      } else {
        cleaned[key] = stripUndefinedInner(val, childPath, stripped);
      }
    }
    return cleaned;
  }
  return value;
};

export const stripUndefined = (value) => {
  const stripped = [];
  const result = stripUndefinedInner(value, '', stripped);
  if (stripped.length > 0) {
    // Cap to avoid flooding the console on a pathological input. The
    // count is still accurate, just the listed paths are truncated.
    const preview = stripped.slice(0, 25);
    console.warn(
      `[stripUndefined] removed ${stripped.length} undefined field(s)${
        stripped.length > preview.length ? ` (first ${preview.length})` : ''
      }:`,
      preview
    );
  }
  return result;
};

/**
 * Loud alternative to `stripUndefined` for write paths whose payload is
 * built from already-cleaned Firestore-read data — currently just the
 * stretch save path (`applyStretchPlanToFirestore`).
 *
 * In dev / Jest / CI (any non-production NODE_ENV) this walks the value
 * and throws on any undefined leaf, naming the exact path. In production
 * builds it returns immediately so we don't pay the deep-walk cost on
 * every save. CRA inlines `process.env.NODE_ENV` at webpack time, so the
 * branch is a constant fold in shipped bundles.
 *
 * Why not just keep stripping? `stripUndefined` silently drops bad
 * fields with a console.warn that nobody reads in production. The
 * stretch path's payload comes from a tightly-controlled splice over
 * Firestore-read data, so an undefined leaf is a bug worth surfacing —
 * not a routine occurrence to silently scrub.
 */
const assertNoUndefinedInner = (value, path) => {
  if (value === undefined) {
    throw new Error(`[assertNoUndefined] undefined at ${path || '<root>'}`);
  }
  if (value === null || typeof value !== 'object') return;
  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i++) {
      assertNoUndefinedInner(value[i], `${path}[${i}]`);
    }
    return;
  }
  if (value.constructor === Object) {
    for (const [k, v] of Object.entries(value)) {
      assertNoUndefinedInner(v, path ? `${path}.${k}` : k);
    }
  }
};

export const assertNoUndefined = (value, label = '') => {
  if (process.env.NODE_ENV === 'production') return value;
  assertNoUndefinedInner(value, label);
  return value;
};
